/**
 * WebSocket-based ConnectionTransport for server-authoritative game mode.
 *
 * This transport sends/receives game messages through the server's socket.io
 * connection, which routes them to/from the Deno game process.
 *
 * Handles automatic reconnection - when the socket reconnects (possibly to a
 * different server instance), it re-emits match:connectEngine to re-establish
 * the player's connection to the Deno process.
 */

import { Socket } from "socket.io-client";
import { getSocket } from "../../shared/api/socket";

/**
 * EventChannel implementation matching the @pretty-chitty/core interface.
 */
export class EventChannel<T> {
  private subscribers: Set<(value: T) => void> = new Set();
  private _value: T;

  constructor(initialValue: T) {
    this._value = initialValue;
  }

  get value(): T {
    return this._value;
  }

  /**
   * Subscribe to value changes.
   * @param cb Callback to invoke when value changes
   * @param notifyImmediate If true, invoke callback immediately with current value
   * @returns Unsubscribe function
   */
  on(cb: (value: T) => void, notifyImmediate: boolean = false): () => void {
    this.subscribers.add(cb);
    if (notifyImmediate) {
      cb(this._value);
    }
    return () => {
      this.subscribers.delete(cb);
    };
  }

  /**
   * Set a new value and notify all subscribers.
   */
  force(newValue: T): void {
    this._value = newValue;
    this.trigger();
  }

  /**
   * Notify all subscribers with the current value.
   */
  trigger(): void {
    this.subscribers.forEach((cb) => {
      try {
        cb(this._value);
      } catch (err) {
        console.error("[EventChannel] Error in subscriber:", err);
      }
    });
  }
}

/**
 * ConnectionTransport interface expected by the game engine.
 * This matches the interface from @pretty-chitty/core.
 */
export interface ConnectionTransport {
  sendMessage(message: unknown): void;
  onReceiveMessage(cb: (message: unknown) => void): () => void;
  onReconnect(cb: (transport: ConnectionTransport) => void): () => void;
  connected: EventChannel<boolean>;
}

/**
 * WebSocket transport that communicates with the server-side Deno process.
 * Manages its own socket connection and handles automatic reconnection.
 */
export class WebSocketTransport implements ConnectionTransport {
  private messageHandlers: Set<(message: unknown) => void> = new Set();
  private reconnectCallbacks: Set<(transport: ConnectionTransport) => void> =
    new Set();
  public connected: EventChannel<boolean>;
  private socket: Socket | null = null;
  private initialConnectionEstablished = false;
  private gameMessageHandler:
    | ((data: {
        matchId: string;
        connectionId: string;
        playerId: string;
        message: unknown;
      }) => void)
    | null = null;
  private versionChangedHandler: ((data: { matchId: string }) => void) | null =
    null;
  private disconnectHandler: (() => void) | null = null;
  private connectHandler: (() => void) | null = null;
  private disposed = false;

  private constructor(private matchId: string) {
    this.connected = new EventChannel<boolean>(false);
  }

  /**
   * Create a new WebSocketTransport and wait for the initial connection.
   */
  static async create(matchId: string): Promise<WebSocketTransport> {
    const transport = new WebSocketTransport(matchId);
    await transport.connect();
    return transport;
  }

  /**
   * Connect to the server and establish engine connection.
   */
  private async connect(): Promise<void> {
    if (this.disposed) return;

    const socket = await getSocket();
    if (this.disposed) return;

    this.socket = socket;

    // Set up message handler
    this.gameMessageHandler = (data) => {
      if (data.matchId === this.matchId) {
        this.messageHandlers.forEach((handler) => {
          try {
            handler(data.message);
          } catch (err) {
            console.error(
              "[WebSocketTransport] Error in message handler:",
              err,
            );
          }
        });
      }
    };
    socket.on("match:gameMessage", this.gameMessageHandler);

    // Handle version changes — server destroyed the old Deno instance,
    // disconnect and reconnect to pick up the new bundle
    this.versionChangedHandler = (data) => {
      if (data.matchId === this.matchId) {
        console.log(
          "[WebSocketTransport] Game version changed, reloading page to pick up new bundle",
        );
        socket.emit("match:disconnectEngine", this.matchId);
        window.location.reload();
      }
    };
    socket.on("match:versionChanged", this.versionChangedHandler);

    // Track connection state and handle reconnection
    this.disconnectHandler = () => {
      console.log("[WebSocketTransport] Socket disconnected");
      this.connected.force(false);
    };
    socket.on("disconnect", this.disconnectHandler);

    this.connectHandler = () => {
      if (!this.initialConnectionEstablished) {
        // Initial connection
        console.log(
          "[WebSocketTransport] Socket connected, establishing engine connection",
        );
        this.initialConnectionEstablished = true;
        this.emitConnectEngine(socket);
      } else {
        // Reconnection — spawn a fresh transport instead of reusing this one
        console.log(
          "[WebSocketTransport] Socket reconnected, spawning new transport",
        );
        this.spawnNewTransport();
      }
    };
    socket.on("connect", this.connectHandler);

    // If already connected, emit immediately
    if (socket.connected) {
      this.initialConnectionEstablished = true;
      this.emitConnectEngine(socket);
    }
  }

  /**
   * Emit match:connectEngine and wait for the server ack before marking
   * the transport as connected. This prevents the game engine from sending
   * messages before the Deno process is wired up.
   */
  private emitConnectEngine(socket: Socket): void {
    socket.emit(
      "match:connectEngine",
      { matchId: this.matchId },
      (response: { ok: boolean }) => {
        if (this.disposed) return;
        if (response?.ok) {
          console.log("[WebSocketTransport] Engine connection established");
          this.connected.force(true);
        } else {
          console.error(
            "[WebSocketTransport] Engine connection failed",
          );
        }
      },
    );
  }

  /**
   * Send a message to the game (routed through server to Deno).
   */
  sendMessage(message: unknown): void {
    if (!this.socket || !this.connected.value) {
      console.warn(
        "[WebSocketTransport] Cannot send message: not connected, buffering",
      );
      // Wait for connected to become true, then send
      const unsub = this.connected.on((connected) => {
        if (connected) {
          unsub();
          this.socket?.emit("match:playerMessage", {
            matchId: this.matchId,
            message,
          });
        }
      });
      return;
    }
    this.socket.emit("match:playerMessage", {
      matchId: this.matchId,
      message,
    });
  }

  /**
   * Register a handler for incoming game messages.
   * Returns an unsubscribe function.
   */
  onReceiveMessage(cb: (message: unknown) => void): () => void {
    this.messageHandlers.add(cb);
    return () => {
      this.messageHandlers.delete(cb);
    };
  }

  /**
   * Register a callback invoked with a fresh ConnectionTransport when the
   * underlying socket reconnects. The current transport is disposed and
   * should not be used after the callback fires.
   * Returns an unsubscribe function.
   */
  onReconnect(cb: (transport: ConnectionTransport) => void): () => void {
    this.reconnectCallbacks.add(cb);
    return () => {
      this.reconnectCallbacks.delete(cb);
    };
  }

  /**
   * Create a brand-new transport for this match and notify all reconnect
   * subscribers. The current transport is cleaned up before callbacks fire.
   */
  private async spawnNewTransport(): Promise<void> {
    const callbacks = [...this.reconnectCallbacks];
    this.cleanupListeners();

    try {
      const newTransport = await WebSocketTransport.create(this.matchId);
      callbacks.forEach((cb) => {
        try {
          cb(newTransport);
        } catch (err) {
          console.error(
            "[WebSocketTransport] Error in reconnect callback:",
            err,
          );
        }
      });
    } catch (err) {
      console.error(
        "[WebSocketTransport] Failed to create new transport on reconnect:",
        err,
      );
    }
  }

  /**
   * Remove all socket listeners and mark this transport as disposed.
   * Does NOT emit match:disconnectEngine (use dispose() for that).
   */
  private cleanupListeners(): void {
    this.disposed = true;

    if (this.socket) {
      if (this.gameMessageHandler) {
        this.socket.off("match:gameMessage", this.gameMessageHandler);
        this.gameMessageHandler = null;
      }
      if (this.versionChangedHandler) {
        this.socket.off("match:versionChanged", this.versionChangedHandler);
        this.versionChangedHandler = null;
      }
      if (this.disconnectHandler) {
        this.socket.off("disconnect", this.disconnectHandler);
        this.disconnectHandler = null;
      }
      if (this.connectHandler) {
        this.socket.off("connect", this.connectHandler);
        this.connectHandler = null;
      }
    }

    this.messageHandlers.clear();
    this.reconnectCallbacks.clear();
  }

  /**
   * Clean up listeners and disconnect from engine.
   */
  dispose(): void {
    if (this.socket) {
      this.socket.emit("match:disconnectEngine", this.matchId);
    }
    this.cleanupListeners();
    this.socket = null;
  }
}
